/**
 * Data layer for `auto-block-publisher-when-fraud-threshold-hit`.
 * Thin typed fetch wrappers over /api/v1/auto-block-publisher-when-fraud-threshold-hit.
 * Raw `fetch()` is confined to this file per the dash contract.
 */

export type BlockStatus = "blocked" | "warned" | "skipped";
export type Severity = "low" | "med" | "high" | "critical";

export interface BlockRow {
	id: number;
	tenant_id: string;
	event_id: string;
	publisher_id: string;
	fraud_count: number;
	threshold: number;
	status: BlockStatus;
	reason: string;
	severity: Severity;
	window_sec: number;
	created_at: number;
}

export interface ListBlockResponse {
	items: BlockRow[];
}

export interface SummaryResponse {
	by_status: { status: "blocked" | "warned"; count: number }[];
	total: number;
}

export interface CreateBlockBody {
	event_id: string;
	publisher_id: string;
	fraud_count: number;
	threshold?: number;
	severity?: Severity;
	window_sec?: number;
	reason?: string;
}

export interface CreateBlockResponse {
	ok: boolean;
	skipped: boolean;
	id?: number;
	tenant_id: string;
	event_id: string;
	publisher_id: string;
	fraud_count: number;
	threshold: number;
	status: BlockStatus;
	reason?: string;
	severity: Severity;
	window_sec: number;
	created_at?: number;
}

const BASE = "/api/v1/auto-block-publisher-when-fraud-threshold-hit";

async function parseOrThrow<T>(res: Response): Promise<T> {
	const text = await res.text();
	let data: unknown = null;
	if (text) {
		try {
			data = JSON.parse(text);
		} catch {
			data = text;
		}
	}
	if (!res.ok) {
		const msg =
			data &&
			typeof data === "object" &&
			data !== null &&
			"error" in data &&
			typeof (data as { error: unknown }).error === "string"
				? (data as { error: string }).error
				: `HTTP ${res.status}`;
		throw new Error(msg);
	}
	return data as T;
}

export async function list(params?: {
	status?: BlockStatus;
	publisher_id?: string;
}): Promise<ListBlockResponse> {
	const qs = new URLSearchParams();
	if (params?.status) qs.set("status", params.status);
	if (params?.publisher_id) qs.set("publisher_id", params.publisher_id);
	const url = qs.toString() ? `${BASE}?${qs.toString()}` : BASE;
	const res = await fetch(url, {
		method: "GET",
		credentials: "include",
		headers: { accept: "application/json" },
	});
	return parseOrThrow<ListBlockResponse>(res);
}

export async function summary(): Promise<SummaryResponse> {
	const res = await fetch(`${BASE}/summary`, {
		method: "GET",
		credentials: "include",
		headers: { accept: "application/json" },
	});
	return parseOrThrow<SummaryResponse>(res);
}

export async function create(body: CreateBlockBody): Promise<CreateBlockResponse> {
	const res = await fetch(BASE, {
		method: "POST",
		credentials: "include",
		headers: {
			accept: "application/json",
			"content-type": "application/json",
		},
		body: JSON.stringify(body),
	});
	return parseOrThrow<CreateBlockResponse>(res);
}

export const autoBlockPublisherWhenFraudThresholdHitQueryKeys = {
	all: ["auto-block-publisher-when-fraud-threshold-hit"] as const,
	list: (params?: { status?: BlockStatus; publisher_id?: string }) =>
		[
			...autoBlockPublisherWhenFraudThresholdHitQueryKeys.all,
			"list",
			params?.status ?? null,
			params?.publisher_id ?? null,
		] as const,
	summary: () =>
		[...autoBlockPublisherWhenFraudThresholdHitQueryKeys.all, "summary"] as const,
};

// ---------- Pure helpers ----------

export const DEFAULT_THRESHOLD = 10;

/**
 * Pure mirror of the BE decision function. Inputs:
 *   - fraud_count: non-negative integer
 *   - threshold:   optional positive integer (default DEFAULT_THRESHOLD)
 * Returns the derived status a POST with the same inputs would produce.
 */
export function predictStatus(
	fraud_count: number,
	threshold: number = DEFAULT_THRESHOLD,
): BlockStatus {
	const t = Number.isFinite(threshold) && threshold > 0 ? Math.floor(threshold) : DEFAULT_THRESHOLD;
	const c = Number.isFinite(fraud_count) ? Math.max(0, Math.floor(fraud_count)) : 0;
	if (c >= t) return "blocked";
	if (c >= t / 2) return "warned";
	return "skipped";
}

/**
 * Pure severity derivation — mirrors BE.
 */
export function predictSeverity(
	fraud_count: number,
	threshold: number = DEFAULT_THRESHOLD,
): Severity {
	const t = Number.isFinite(threshold) && threshold > 0 ? Math.floor(threshold) : DEFAULT_THRESHOLD;
	const c = Number.isFinite(fraud_count) ? Math.max(0, fraud_count) : 0;
	const ratio = t > 0 ? c / t : 0;
	if (ratio >= 2) return "critical";
	if (ratio >= 1) return "high";
	if (ratio >= 0.5) return "med";
	return "low";
}
